/**
 * Adapter layer between chatmcp types and use-hapimcp hook
 * Maps chatmcp's MCPServer, ChatMessage, LLMSettings to use-hapimcp's types
 */

import { useEffect, useRef, useMemo, useCallback } from 'react';
import { useMcpChat } from 'use-hapimcp';
import type {
  UseMcpChatConfig,
  ChatMessage as HookChatMessage,
  AgentActionLog,
} from 'use-hapimcp';
import type {
  MCPServer,
  ChatMessage as AppChatMessage,
  LLMSettings,
  MCPTool,
} from '@/types';
import { log } from '@/lib/utils';
import { ChatMcpProvider } from './ChatMcpProvider';

/**
 * Convert chatmcp ChatMessage to use-hapimcp ChatMessage
 */
export const toHookMessage = (msg: AppChatMessage): HookChatMessage => ({
  id: msg.id,
  role: msg.author === 'agent' ? 'assistant' : msg.author === 'user' ? 'user' : 'system',
  content: msg.content,
  timestamp: msg.createdAt,
  metadata: {
    ...msg.meta,
    tags: msg.tags,
    status: msg.status,
    contactId: msg.contactId,
  },
});

/**
 * Convert use-hapimcp ChatMessage to chatmcp ChatMessage
 */
export const toAppMessage = (msg: HookChatMessage, contactId: string): AppChatMessage => ({
  id: msg.id,
  contactId,
  author: msg.role === 'assistant' ? 'agent' : msg.role === 'user' ? 'user' : 'system',
  content: msg.content,
  createdAt: msg.timestamp,
  status: msg.metadata?.status as any ?? 'sent',
  tags: msg.metadata?.tags as string[] | undefined,
  meta: msg.metadata,
});

/**
 * Hook wrapper that provides chatmcp-compatible API
 */
export function useMcpChatAdapter(config: {
  contact: MCPServer;
  initialMessages: AppChatMessage[];
  llmSettings: LLMSettings;
  onMessagesChange: (messages: AppChatMessage[]) => void;
  onAction?: (action: AgentActionLog) => void;
}) {
  const { contact, initialMessages, llmSettings, onMessagesChange, onAction } = config;
  
  // Store callback in ref to avoid dependency issues
  const onMessagesChangeRef = useRef(onMessagesChange);
  onMessagesChangeRef.current = onMessagesChange;
  
  // Track last notified message IDs to prevent duplicate notifications
  const lastNotifiedIdsRef = useRef<string>('');

  // Memoize LLM provider to prevent recreating on every render
  const chatMcpProvider = useMemo(() => new ChatMcpProvider({
    name: 'chatmcp-provider',
    llmSettings,
    contact,
  }), [contact.id, llmSettings.model, llmSettings.apiKey]);

  // Memoize onAction callback
  const handleAction = useCallback((action: AgentActionLog) => {
    log.debug('[useMcpChatAdapter] Agent action:', action);
    onAction?.(action);
  }, [onAction]);

  // Configure hook - memoize to prevent recreation
  const hookConfig = useMemo((): UseMcpChatConfig => ({
    sessionId: contact.id,
    mcp: {
      baseUrl: contact.url,
      headers: contact.headers,
      authToken: contact.token,
      timeout: 30000,
    },
    llmProviders: {
      'chatmcp-provider': chatMcpProvider,
    },
    initialLlmId: 'chatmcp-provider',
    llmConfigs: {
      'chatmcp-provider': {
        baseUrl: llmSettings.baseUrl || (llmSettings.provider === 'ollama' ? 'http://localhost:11434' : ''),
        model: llmSettings.model,
        apiKey: llmSettings.apiKey,
        headers: {},
        temperature: llmSettings.temperature,
        maxTokens: llmSettings.maxTokens,
      },
    },
    initialRules: [],
    onAction: handleAction,
    onError: (error) => {
      log.error('[useMcpChatAdapter] Hook error:', error);
    },
  }), [
    contact.id,
    contact.url,
    JSON.stringify(contact.headers),
    contact.token,
    chatMcpProvider,
    JSON.stringify(llmSettings),
    handleAction,
  ]);

  // Use the hook
  const hook = useMcpChat(hookConfig);

  // Convert messages back to app format and notify parent - only when messages actually change
  useEffect(() => {
    // Create a signature of current messages to detect actual changes
    const currentIds = hook.messages.map(m => m.id).join(',');
    
    // Only notify if messages have actually changed
    if (currentIds !== lastNotifiedIdsRef.current) {
      lastNotifiedIdsRef.current = currentIds;
      const appMessages = hook.messages.map((msg) => toAppMessage(msg, contact.id));
      log.debug('[useMcpChatAdapter] Messages changed, notifying parent:', appMessages.length);
      onMessagesChangeRef.current(appMessages);
    }
  }, [hook.messages, contact.id]);

  // Memoize appTools to prevent unnecessary re-renders
  const appTools = useMemo(() => hook.tools.map((tool): MCPTool => ({
    id: tool.name,
    name: tool.name,
    description: tool.description,
    inputSchema: tool.inputSchema,
    serverId: contact.id,
    sourceServer: contact.name,
    enabled: true,
  })), [hook.tools, contact.id, contact.name]);

  // Memoize sendMessage to keep stable reference
  const sendMessage = useCallback(async (content: string) => {
    await hook.sendUserMessage(content);
  }, [hook.sendUserMessage]);

  return {
    ...hook,
    // Provide app-compatible send function
    sendMessage,
    // Map tools to app format
    appTools,
  };
}
